import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, Loader2, Package, Mail, DollarSign, ShoppingCart, RefreshCw, Download, Trash2, Percent, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WC = { withCredentials: true };

const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s || "—"; } };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const downloadCSV = (filename, rows, columns) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(typeof c.get === "function" ? c.get(r) : r[c.key])).join(",")).join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const StatusBadge = ({ status }) => {
  const paid = status === "paid";
  return (
    <span className={`text-xs rounded-full px-2.5 py-1 ${paid ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-white/60"}`}>
      {status || "pending"}
    </span>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking session, true, false
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [codeType, setCodeType] = useState("percent");
  const [codeValue, setCodeValue] = useState("");
  const [firstOrderOnly, setFirstOrderOnly] = useState(false);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/admin/logout`, {}, WC); } catch { /* ignore */ }
    setAuthed(false);
    setStats(null); setOrders([]); setWaitlist([]);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await axios.post(`${API}/admin/login`, { password }, WC);
      setAuthed(true);
      setPassword("");
    } catch (err) {
      toast.error(err?.response?.status === 401 ? "Incorrect password." : "Login failed.");
    } finally {
      setLoggingIn(false);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, w, c] = await Promise.all([
        axios.get(`${API}/admin/stats`, WC),
        axios.get(`${API}/admin/orders`, WC),
        axios.get(`${API}/admin/waitlist`, WC),
        axios.get(`${API}/admin/codes`, WC),
      ]);
      setStats(s.data); setOrders(o.data); setWaitlist(w.data); setCodes(c.data);
    } catch (err) {
      if (err?.response?.status === 401) { toast.error("Session expired. Please log in."); setAuthed(false); }
      else toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/admin/me`, WC)
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => { if (authed) loadData(); }, [authed, loadData]);

  const createCode = async () => {
    const c = newCode.trim().toUpperCase();
    const v = parseFloat(codeValue);
    if (!c || !v || v <= 0) { toast.error("Enter a code and a value greater than 0."); return; }
    if (codeType === "percent" && v > 100) { toast.error("Percent cannot exceed 100."); return; }
    try {
      await axios.post(`${API}/admin/codes`, { code: c, type: codeType, value: v, first_order_only: firstOrderOnly }, WC);
      setNewCode(""); setCodeValue(""); setFirstOrderOnly(false);
      toast.success(`Code ${c} saved.`);
      loadData();
    } catch (err) { toast.error("Could not save code."); }
  };

  const deleteCode = async (c) => {
    try {
      await axios.delete(`${API}/admin/codes/${c}`, WC);
      toast.success(`Code ${c} deleted.`);
      loadData();
    } catch (err) { toast.error("Could not delete code."); }
  };

  const toggleFeatured = async (c) => {
    const isFeatured = codes.find((x) => x.code === c)?.featured;
    try {
      await axios.post(`${API}/admin/featured-code`, { code: isFeatured ? null : c }, WC);
      toast.success(isFeatured ? "Launch banner cleared." : `${c} is now the launch banner.`);
      loadData();
    } catch (err) { toast.error("Could not update the launch banner."); }
  };

  const exportOrders = () => downloadCSV("incognito-orders.csv", orders, [
    { label: "Date", get: (r) => fmtDate(r.created_at) },
    { label: "Product", key: "product_name" },
    { label: "Color", key: "color" },
    { label: "Magnification", key: "strength" },
    { label: "Quantity", key: "quantity" },
    { label: "Total", get: (r) => Number(r.amount || 0).toFixed(2) },
    { label: "Discount code", get: (r) => r.discount_code || "" },
    { label: "Provider", key: "provider" },
    { label: "Payment status", key: "payment_status" },
    { label: "Customer email", get: (r) => r.email || "" },
    { label: "Customer phone", get: (r) => r.phone || "" },
  ]);

  const exportWaitlist = () => downloadCSV("incognito-waitlist.csv", waitlist, [
    { label: "Date", get: (r) => fmtDate(r.created_at) },
    { label: "Email", key: "email" },
    { label: "Interest", key: "interest" },
  ]);

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center" data-testid="admin-checking">
        <Loader2 className="h-8 w-8 animate-spin text-white/50" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl bg-[#121212] border border-white/10 p-8" data-testid="admin-login">
          <Lock className="h-7 w-7 text-[#8BA3B3]" />
          <h1 className="font-heading text-2xl mt-5 tracking-tight">Admin Access</h1>
          <p className="text-white/50 text-sm mt-2">Enter your admin password to view orders and signups.</p>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" data-testid="admin-password"
            className="mt-6 h-12 rounded-xl bg-black border-white/20 text-white px-4 focus-visible:ring-white/30" />
          <Button type="submit" disabled={loggingIn} data-testid="admin-login-button"
            className="mt-4 w-full h-12 rounded-xl bg-white text-black hover:bg-white/85 font-semibold">
            {loggingIn ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
          </Button>
        </form>
      </div>
    );
  }

  const statCards = [
    { label: "Revenue (paid)", value: money(stats?.revenue), icon: DollarSign },
    { label: "Paid orders", value: stats?.paid_orders ?? "—", icon: ShoppingCart },
    { label: "Total orders", value: stats?.total_orders ?? "—", icon: Package },
    { label: "Waitlist", value: stats?.waitlist_count ?? "—", icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-10 h-16 flex items-center justify-between">
        <span className="font-heading text-lg tracking-[0.3em]">INCOGNITO · ADMIN</span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => loadData()} data-testid="admin-refresh"
            className="text-white/70 hover:text-white hover:bg-white/10 rounded-full h-9">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="ghost" onClick={logout} data-testid="admin-logout"
            className="text-white/70 hover:text-white hover:bg-white/10 rounded-full h-9">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="px-6 md:px-10 py-8 max-w-6xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((c) => (
            <div key={c.label} className="rounded-2xl bg-[#121212] border border-white/10 p-5" data-testid={`stat-${c.label}`}>
              <c.icon className="h-5 w-5 text-[#8BA3B3]" />
              <p className="text-2xl font-semibold mt-3">{c.value}</p>
              <p className="text-white/50 text-sm mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="orders" className="mt-8">
          <TabsList className="bg-[#121212] border border-white/10">
            <TabsTrigger value="orders" data-testid="tab-orders">Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist">Waitlist ({waitlist.length})</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts">Discounts ({codes.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-4">
            <div className="flex justify-end mb-3">
              <Button onClick={exportOrders} variant="outline" data-testid="export-orders"
                className="rounded-full border-white/20 bg-transparent text-white/80 hover:bg-white/10 h-9">
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>
            <div className="rounded-2xl bg-[#121212] border border-white/10 overflow-x-auto" data-testid="orders-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/50 text-left border-b border-white/10">
                    <th className="p-4 font-normal">Date</th>
                    <th className="p-4 font-normal">Product</th>
                    <th className="p-4 font-normal">Options</th>
                    <th className="p-4 font-normal">Qty</th>
                    <th className="p-4 font-normal">Total</th>
                    <th className="p-4 font-normal">Via</th>
                    <th className="p-4 font-normal">Status</th>
                    <th className="p-4 font-normal">Customer</th>
                    <th className="p-4 font-normal">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 && (
                    <tr><td colSpan={9} className="p-8 text-center text-white/40">No orders yet.</td></tr>
                  )}
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                      <td className="p-4 text-white/60 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                      <td className="p-4">{o.product_name}</td>
                      <td className="p-4 text-white/60">{o.color} · {o.strength}</td>
                      <td className="p-4">{o.quantity}</td>
                      <td className="p-4 font-medium">{money(o.amount)}</td>
                      <td className="p-4"><Badge variant="outline" className="border-white/20 text-white/70 capitalize">{o.provider || "—"}</Badge></td>
                      <td className="p-4"><StatusBadge status={o.payment_status} /></td>
                      <td className="p-4 text-white/60">{o.email || "—"}</td>
                      <td className="p-4 text-white/60">{o.phone || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="waitlist" className="mt-4">
            <div className="flex justify-end mb-3">
              <Button onClick={exportWaitlist} variant="outline" data-testid="export-waitlist"
                className="rounded-full border-white/20 bg-transparent text-white/80 hover:bg-white/10 h-9">
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>
            <div className="rounded-2xl bg-[#121212] border border-white/10 overflow-x-auto" data-testid="waitlist-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/50 text-left border-b border-white/10">
                    <th className="p-4 font-normal">Date</th>
                    <th className="p-4 font-normal">Email</th>
                    <th className="p-4 font-normal">Interest</th>
                  </tr>
                </thead>
                <tbody>
                  {waitlist.length === 0 && (
                    <tr><td colSpan={3} className="p-8 text-center text-white/40">No signups yet.</td></tr>
                  )}
                  {waitlist.map((w) => (
                    <tr key={w.email} className="border-b border-white/5 hover:bg-white/[0.03]">
                      <td className="p-4 text-white/60 whitespace-nowrap">{fmtDate(w.created_at)}</td>
                      <td className="p-4">{w.email}</td>
                      <td className="p-4 text-white/60 capitalize">{w.interest}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="discounts" className="mt-4">
            <div className="rounded-2xl bg-[#121212] border border-white/10 p-6 mb-4" data-testid="code-creator">
              <p className="text-sm uppercase tracking-widest text-white/50 mb-4">Create discount code</p>
              <div className="flex flex-col md:flex-row gap-3 md:items-center">
                <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="CODE (e.g. SUMMER10)"
                  data-testid="code-input" className="h-11 rounded-xl bg-black border-white/20 text-white px-4 uppercase md:w-56" />
                <div className="inline-flex rounded-xl border border-white/20 overflow-hidden">
                  <button onClick={() => setCodeType("percent")} data-testid="code-type-percent"
                    className={`px-4 h-11 text-sm ${codeType === "percent" ? "bg-white text-black" : "text-white/70"}`}>% off</button>
                  <button onClick={() => setCodeType("fixed")} data-testid="code-type-fixed"
                    className={`px-4 h-11 text-sm ${codeType === "fixed" ? "bg-white text-black" : "text-white/70"}`}>$ off</button>
                </div>
                <Input value={codeValue} onChange={(e) => setCodeValue(e.target.value)} type="number" placeholder={codeType === "percent" ? "20" : "5.00"}
                  data-testid="code-value" className="h-11 rounded-xl bg-black border-white/20 text-white px-4 md:w-32" />
                <Button onClick={createCode} data-testid="code-create"
                  className="h-11 rounded-xl bg-white text-black hover:bg-white/85 font-semibold px-6">Add Code</Button>
              </div>
              <button onClick={() => setFirstOrderOnly((v) => !v)} data-testid="code-first-order-toggle"
                className="mt-4 flex items-center gap-2 text-sm text-white/60 hover:text-white">
                <span className={`h-5 w-5 rounded border flex items-center justify-center ${firstOrderOnly ? "bg-white border-white text-black" : "border-white/30"}`}>
                  {firstOrderOnly ? "✓" : ""}
                </span>
                First order only (one-time per customer email)
              </button>
            </div>

            <div className="rounded-2xl bg-[#121212] border border-white/10 overflow-x-auto" data-testid="codes-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/50 text-left border-b border-white/10">
                    <th className="p-4 font-normal">Code</th>
                    <th className="p-4 font-normal">Discount</th>
                    <th className="p-4 font-normal">Paid uses</th>
                    <th className="p-4 font-normal">Attempts</th>
                    <th className="p-4 font-normal">Discount given</th>
                    <th className="p-4 font-normal">Banner</th>
                    <th className="p-4 font-normal">Created</th>
                    <th className="p-4 font-normal"></th>
                  </tr>
                </thead>
                <tbody>
                  {codes.length === 0 && (
                    <tr><td colSpan={8} className="p-8 text-center text-white/40">No codes yet. Create one above.</td></tr>
                  )}
                  {codes.map((c) => (
                    <tr key={c.code} className="border-b border-white/5 hover:bg-white/[0.03]">
                      <td className="p-4 font-medium tracking-wide">
                        {c.code}
                        {c.first_order_only && <span className="ml-2 text-[10px] uppercase tracking-wide bg-white/10 text-white/60 rounded-full px-2 py-0.5">1st order</span>}
                      </td>
                      <td className="p-4 text-white/70">{c.type === "bogo" ? "2nd pair 50% off" : c.type === "percent" ? `${c.value}% off` : `${money(c.value)} off`}</td>
                      <td className="p-4 text-white/80" data-testid={`code-uses-${c.code}`}>{c.paid_uses ?? 0}</td>
                      <td className="p-4 text-white/50">{c.attempts ?? 0}</td>
                      <td className="p-4 text-white/70">{money(c.discount_given)}</td>
                      <td className="p-4">
                        <Button onClick={() => toggleFeatured(c.code)} variant="ghost" data-testid={`code-feature-${c.code}`}
                          className={`rounded-full h-8 w-8 p-0 ${c.featured ? "text-yellow-400 hover:text-yellow-300" : "text-white/30 hover:text-white/70"} hover:bg-white/10`}>
                          <Star className={`h-4 w-4 ${c.featured ? "fill-yellow-400" : ""}`} />
                        </Button>
                      </td>
                      <td className="p-4 text-white/60 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                      <td className="p-4 text-right">
                        <Button onClick={() => deleteCode(c.code)} variant="ghost" data-testid={`code-delete-${c.code}`}
                          className="text-white/50 hover:text-red-400 hover:bg-red-500/10 rounded-full h-8 w-8 p-0">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
